import { useEffect, useRef } from 'react';

/**
 * Cloudflare Turnstile, shown only when VITE_TURNSTILE_SITE_KEY is set (the
 * API enforces it only when TURNSTILE_SECRET_KEY is set: set both or neither).
 * Loads Cloudflare's script on first use, never on other pages.
 */
interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id?: string) => void;
  remove: (id: string) => void;
}
declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export function turnstileSiteKey(): string | null {
  return import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim() || null;
}

let loading: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  loading ??= new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      loading = null;
      reject(new Error('turnstile script'));
    };
    document.head.appendChild(s);
  });
  return loading;
}

export function Turnstile({
  lang,
  onToken,
  resetKey,
}: {
  lang: 'he' | 'en';
  onToken: (token: string | null) => void;
  /** Change it to get a fresh token (after a refused submit). */
  resetKey: number;
}) {
  const el = useRef<HTMLDivElement>(null);
  const id = useRef<string | null>(null);
  const siteKey = turnstileSiteKey();

  useEffect(() => {
    if (!siteKey) return;
    let gone = false;
    loadScript()
      .then(() => {
        if (gone || !el.current || !window.turnstile) return;
        id.current = window.turnstile.render(el.current, {
          sitekey: siteKey,
          // The API accepts only tokens issued for this action (TOUR_TURNSTILE_ACTION).
          action: 'tour_book',
          language: lang,
          theme: 'light',
          callback: (t: string) => onToken(t),
          'expired-callback': () => onToken(null),
          'error-callback': () => onToken(null),
        });
      })
      .catch(() => onToken(null));
    return () => {
      gone = true;
      if (id.current && window.turnstile) window.turnstile.remove(id.current);
      id.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey, lang]);

  useEffect(() => {
    if (resetKey && id.current && window.turnstile) {
      window.turnstile.reset(id.current);
      onToken(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  if (!siteKey) return null;
  return <div ref={el} className="mt-4 min-h-[65px]" data-testid="turnstile" />;
}
